/**
 * 从bihu/common-js中迁移的公共函数
 */
// import BigNumber from 'bignumber.js'

/**
* 防抖
* @param {Function} func 回调函数
* @param {number} waitTime 防抖时间(毫秒)
* @param {boolean} firstIsDebounce 第一次是否防抖，默认true
* @returns {Function} 转换后的函数
*/
let defaultWaitTime = 300 // 默认防抖时间（ms）
export function debounce<F extends(...args: any) => any>(func: F, waitTime?: number, firstIsDebounce?: boolean): (...args: Parameters<F>) => void {
  if (waitTime === undefined) {
    waitTime = defaultWaitTime
  }
  if (firstIsDebounce === undefined) {
    firstIsDebounce = true
  }
  let timeout: any
  return function(...args: any[]) {
    if (timeout && typeof Number(timeout) === 'number') {
      clearTimeout(timeout)
    }
    if (firstIsDebounce) {
      timeout = setTimeout(() => {
        func(...args)
      }, waitTime)
    } else {
      func(...args)
      firstIsDebounce = true
    }
  }
}

interface FillZeroOptions {
  isFillBack?: boolean;
  truncate?: boolean;
}

/**
 * 字符串填充「0」
 * @param {string | number} origin 原始字符串
 * @param {number} length 目标字符串长度
 * @param {FillZeroOptions} options 其他参数
 * @param {boolean} [options.isFillBack=false] 是否从尾部填充，默认从头部填充
 * @param {boolean} [options.truncate=false] 超出时是否截断
 * @returns {string} 处理后的字符串
 */
export function fillZero(origin: string | number, length: number, options?: FillZeroOptions): string {
  const { isFillBack = false, truncate = false } = options || {}

  let originStr = String(origin)
  if (!truncate) {
    // 若原长度比目标长度更长，且 truncate 为 false，则不截断
    length = originStr.length > length ? originStr.length : length
  }
  if (isFillBack) {
    return (originStr + new Array(length).join('0')).substring(0, length)
  } else {
    return (new Array(length).join('0') + origin.toString()).slice(-length)
  }
}

/**
 * 时间类型兼容，兼容「时间戳数字」「时间戳字符串」和「时间对象」
 * @param {string|number|Date} time 时间内容，可传入时间对象，或时间戳
 * @param {DealTimeType} type 期望返回的数据类型
 * @returns {string|Date} 时间对象或时间戳
 */
type TimeType = string | number | Date;
export function dealTime(time: TimeType | 0 | '', type?: 'string' | 'object'): number | Date | '' | null {
  if (type === undefined) {
    type = 'string'
  }
  if (!time) {
    return type === 'string' ? '' : null
  }
  if (time.toString().indexOf('/') > 0 || time.toString().indexOf('-') > 0) {
    time = new Date(time)
  } else if (typeof time === 'string' || typeof time === 'number') {
    let standTimeStampLength = 13 // 标准时间戳长度
    time = fillZero(time, standTimeStampLength, { isFillBack: true }) // 转化为标准的13位时间戳
    time = new Date(parseInt(time))
  }
  if (type === 'object') {
    return time
  } else {
    return time.getTime()
  }
}

/**
* 格式化时间
* @param {string|number|Date} date 时间内容，可传入时间对象，或时间戳
* @param {string} formatStr 格式模板
* @returns {string} 处理后的字符串
*/
export function formatTime(date: string | number | Date, formatStr?: string): string {
  if (formatStr === undefined) {
    formatStr = 'YYYY-MM-DD hh:mm:ss'
  }
  if (!date) {
    return ''
  }
  const nDate = dealTime(date, 'object')
  if (nDate === null || nDate.toString() === 'Invalid Date') {
    return ''
  }
  let formatType = {
    Y: nDate instanceof Date ? nDate.getFullYear() : 0,
    M: nDate instanceof Date ? nDate.getMonth() + 1 : 0,
    D: nDate instanceof Date ? nDate.getDate() : 0,
    h: nDate instanceof Date ? nDate.getHours() : 0,
    m: nDate instanceof Date ? nDate.getMinutes() : 0,
    s: nDate instanceof Date ? nDate.getSeconds() : 0,
  }
  return formatStr.replace(/Y+|M+|D+|h+|m+|s+/g, target => {
    return fillZero(formatType[target[0] as keyof typeof formatType], target.length, { truncate: true })
  })
}

/** 是否在服务器端 */
export const isServer: boolean = typeof window === 'undefined'
export const isUserAgent = (isServer ? {} : window.navigator).userAgent
export const userAgent = isUserAgent === undefined ? '' : isUserAgent

export function getBrowserType() {
  if (!isServer && (typeof (window as any).ActiveXObject !== 'undefined' || 'ActiveXObject' in window)) { // IE
    let reIE = new RegExp('MSIE (\\d+\\.\\d+)')
    reIE.test(userAgent)
    let fIEVersion = parseFloat(RegExp.$1)
    return 'IE'.concat(fIEVersion.toString() || '低版本')
  } else if (userAgent.indexOf('Firefox') !== -1) {
    return 'Firefox(火狐)'
  } else if (userAgent.indexOf('Opera') !== -1) {
    return 'Opera'
  } else if (userAgent.indexOf('Edge') !== -1) {
    return 'Edge'
  } else if (userAgent.indexOf('QQ') !== -1 && userAgent.indexOf('QQBrowser') === 1) {
    return '手机QQ'
  } else if (userAgent.indexOf('QQBrowser') !== -1) {
    return 'QQ浏览器'
  } else if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('MetaSr') !== -1) {
    return '搜狗浏览器'
  } else if (userAgent.indexOf('MicroMessenger') !== -1) {
    return '微信浏览器'
  } else if (userAgent.indexOf('LBBROWSER') !== -1) {
    return '猎豹浏览器'
  } else if (userAgent.indexOf('Maxthon') !== -1) {
    return '遨游浏览器'
  } else if (userAgent.indexOf('TheWorld') !== -1) {
    return '世界之窗浏览器'
  } else if (userAgent.indexOf('bWeibo') !== -1) {
    return '微博'
  } else if (userAgent.indexOf('ubrowser') !== -1) {
    return 'UC'
  } else if (userAgent.indexOf('bidubrowser') !== -1) {
    return '百度'
  } else if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
    return 'Safari'
  } else if (userAgent.indexOf('Chrome') !== -1) {
    return 'Chrome'
  } else {
    return ''
  }
}
export const browserType: string = getBrowserType()

/**
 * 解析URL参数，并进行URL参数解码
 * @param {string} [url] 解析对象
 * @param {string} [key] 需要获取的参数名，不传则返回整个解析的对象
 * @returns {string|object|undefined} 返回整个解决后的对象，或者特定Key的值
 */
export function getParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(url?: string, key?: string): T[keyof T] | T | undefined {
  // 如果没有提供url，则默认使用当前页面的URL
  if (url === undefined) {
    url = window.location.href
  }

  // 初始化参数对象
  const params: Record<string, string | undefined> = {}

  // 对URL进行解码
  url = decodeURIComponent(url)

  if (url === 'invalid-url') {
    return {} as T
  }

  // 检查是否支持URL和URLSearchParams
  if (typeof URL === 'function' && typeof URLSearchParams === 'function') {
    let searchParams: URLSearchParams

    // 判断是否是查询字符串
    if (/^(\?|$)/.test(url)) {
      // 如果是查询字符串，直接使用URLSearchParams
      searchParams = new URLSearchParams(url)
    } else {
      // 如果是完整的URL，先解析URL，再获取查询参数
      const parsedUrl = new URL(url)
      // eslint-disable-next-line prefer-destructuring
      searchParams = parsedUrl.searchParams
    }

    // 遍历查询参数并添加到params对象中
    searchParams.forEach((value, paramName) => {
      params[paramName] = value
    })
  } else {
    // 如果不支持URL和URLSearchParams，使用正则表达式解析
    url.replace(/[?&](\w+)=([^&?]*)/g, (matchStr: any, paramName: any, value: any) => {
      params[paramName] = value
      return matchStr
    })
  }

  // 如果提供了key，则返回该key对应的值，否则返回整个参数对象
  return key ? (params[key] as T[keyof T]) : (params as T)
}

/**
 * 获取URL中的文件后缀
 * @param {string} url 数据源的URL
 * @returns {string} 解析后的文件后缀
 */
export function getSuffix(url: string): string {
  if (!url) {
    return ''
  }
  let result = decodeURIComponent(url).match(/\.(\w+?)(?=(\?|$))/)
  return result ? result[1] : ''
}

/**
 * isPhone 手机号检测
 * @param {number | string} text 待检测字符串
 * @returns {boolean} 是否手机号
 */
export function isPhone(text: number | string): boolean {
  return /^1[3-9]\d{9}$/.test(text.toString().trim())
}

/**
* 保留若干位小数
* @param {number|string} number 数值
* @param {modeType} [mode=floor] 舍入模式，floor 是向下取整，round 是四舍五入，ceil 是向上进一位
* @param {number} [decimals=2] 需要保留几位小数，默认 2
* @returns {number} 保留后数值
*/
// type modeType = 'floor' | 'round' | 'ceil';
// export function keep2Decimals(number: number, mode?: modeType, decimals?: number): number {
//   if (Math.abs(number) === 0) {
//     number = 0
//   }
//   if (mode === undefined) {
//     mode = 'floor'
//   }
//   if (decimals === undefined) {
//     decimals = 2
//   }
//   // eslint-disable-next-line no-magic-numbers
//   let times = Math.pow(10, decimals)
//   let oneHundredTimes = new BigNumber(number).times(times)
//   // https://mikemcl.github.io/bignumber.js/#constructor-properties
//   let roundModes = {
//     ceil: BigNumber.ROUND_CEIL,
//     floor: BigNumber.ROUND_FLOOR,
//     round: BigNumber.ROUND_HALF_UP, // 四舍五入
//   }
//   let roundMode = roundModes[mode]
//   if (!Object.prototype.hasOwnProperty.call(roundModes, mode)) {
//     throw new Error('\u6682\u4E0D\u652F\u6301 '.concat(mode, ' \u820D\u5165\u6A21\u5F0F'))
//   }
//   let integer = oneHundredTimes.integerValue(roundMode)
//   let newNumber = new BigNumber(integer).dividedBy(times)
//   return newNumber.toNumber()
// }

/**
* 格式化数字，每隔三位添加逗号
* @param {number} num 数值
* @returns {string} 格式化后的数值字符串
*/
export function parseNum(num: number): string {
  let numberString = (num || 0).toString()
  // 在转换一些以万为单位的金额时，小数点位数会超过3位，小数点中的数字暂不解析
  let decimalPointIndex = numberString.indexOf('.')
  let intString = decimalPointIndex > -1 ? numberString.slice(0, decimalPointIndex) : numberString
  let decimalString = decimalPointIndex > -1 ? numberString.slice(decimalPointIndex) : ''
  return intString.replace(/(\d)(?=(?:\d{3})+($|\.))/g, '$1,') + decimalString
}

/**
 * 获取随机字符串
 * @param {number} length 字符串长度
 * @param {string} [type] 随机字符串类型，letter纯字母，number纯数字，mix字母与数字混合
 * @param {string} [prefix] 字符串前缀
 * @returns {string} 生成的随机字符串
 */
export function randomString(length: number, type?: string, prefix?: string): string {
  if (type === undefined) {
    type = 'letter'
  }
  if (prefix === undefined) {
    prefix = ''
  }
  let numbers = '0123456789'
  let letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  let chars = ''
  let result = prefix
  if (type === 'number' || type === 'mix') {
    chars += numbers
  }
  if (type === 'letter' || type === 'mix') {
    chars += letters
  }
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}

/**
* 根据 blob 或 url 直接下载文件
* @param {string|Blob} url 获取文件对应的URL
* @param {string} [fileName] 文件名，仅对同源 url 生效。可以直接带后缀，这样就可以免除第三个参数
* @param {string} [fileType] 文件类型
*/
export function download(url: Blob | string, fileName?: string, fileType?: string): void {
  let isBlob = url instanceof Blob
  let targetUrl: string
  if (url instanceof Blob) {
    targetUrl = URL.createObjectURL(url)
  } else {
    targetUrl = url
  }
  let a = document.createElement('a')
  let downloadValue = fileName ? [fileName, fileType].filter(Boolean).join('.') : ''
  downloadValue && (a.download = downloadValue)
  a.href = targetUrl
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.parentNode && a.parentNode.removeChild(a)
  if (isBlob) {
    URL.revokeObjectURL(targetUrl)
  }
}

export const downloadImage = (url: string, fileName?: string, fileType?: string) => {
  const xhr = new XMLHttpRequest();
  xhr.responseType = 'arraybuffer'
  xhr.open('GET', url)
  xhr.send()
  xhr.onload = function () {
    const fileBlob = new Blob([xhr.response], { type: 'application/force-download' });
    console.log('fileBlob', fileBlob)
    download(fileBlob, fileName, fileType)
  }
  
}

/**
* 校验身份证号码（只支持18位）
* @param  {String}   IDNumber  待校验的身份证号码字符串
* @return {Boolean}            校验结果，合法为 true，反之则为 false
*/
export function validateIDNumber(IDNumber: string): boolean {
  let city = {
    11: '北京',
    12: '天津',
    13: '河北',
    14: '山西',
    15: '内蒙古',
    21: '辽宁',
    22: '吉林',
    23: '黑龙江 ',
    31: '上海',
    32: '江苏',
    33: '浙江',
    34: '安徽',
    35: '福建',
    36: '江西',
    37: '山东',
    41: '河南',
    42: '湖北 ',
    43: '湖南',
    44: '广东',
    45: '广西',
    46: '海南',
    50: '重庆',
    51: '四川',
    52: '贵州',
    53: '云南',
    54: '西藏 ',
    61: '陕西',
    62: '甘肃',
    63: '青海',
    64: '宁夏',
    65: '新疆',
    71: '台湾',
    81: '香港',
    82: '澳门',
    91: '国外'
  }
  // 截取身份证的出生日期
  let birthday = `${IDNumber.substring(6, 10)}/${IDNumber.substring(10, 12)}/${IDNumber.substring(12, 14)}`
  let d = new Date(birthday)

  let newBirthday = `${d.getFullYear()}/${(d.getMonth() + 1) > 9 ? d.getMonth() + 1 : ''.concat('0', String(d.getMonth() + 1))}/${d.getDate() > 9 ? d.getDate() : ''.concat('0', String(d.getDate()))}`
  let currentTime = new Date().getTime()
  let time = d.getTime()
  let arrInt = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]
  let arrCh = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2']
  if (!/^\d{17}(\d|x)$/i.test(IDNumber)) {
    return false
  }
  if (city[IDNumber.substring(0, 2) as unknown as keyof typeof city] === undefined) {
    return false
  }
  if (time >= currentTime || birthday !== newBirthday) {
    return false
  }
  let sum = 0
  for (let i = 0; i < 17; i++) {
    sum = sum + Number(IDNumber.substring(i, i + 1)) * arrInt[i]
  }
  let residue = arrCh[sum % 11]
  if (residue !== IDNumber.substring(17, 18).toUpperCase()) {
    return false
  }
  return true
}

/**
* 除去多余参数
* @param {AnyObj} obj 需要处理的对象
* @param {boolean} isFillEmptyStr 是否填充为空字符串
* @returns {object} 处理完成后的对象
*/
interface AnyObj {
  [propName: string | number]: any;
}
export function deleteEmpty(obj: AnyObj, isFillEmptyStr?: boolean): AnyObj {
  if (isFillEmptyStr === undefined) {
    isFillEmptyStr = false
  }
  obj = JSON.parse(JSON.stringify(obj))
  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].trim()
    }
    if (obj[key] === undefined || obj[key] === null || obj[key] === '') {
      if (isFillEmptyStr) {
        obj[key] = ''
      } else {
        delete obj[key]
      }
    }
    if (typeof obj[key] === 'object' && obj[key].constructor !== Array) {
      obj[key] = deleteEmpty(obj[key], isFillEmptyStr)
    }
  })
  return obj
}

/**
 * 加载JS文件
 * @param {string} url JS路径
 * @returns {Promise<any>} 加载成功的回调
 */
export function loadScript(url: string): Promise<boolean | ErrorEvent> {
  return new Promise((resolve, reject) => {
    let $script = document.createElement('script')
    $script.src = url
    $script.async = true
    $script.addEventListener('error', e => {
      reject(e)
    })
    $script.addEventListener('load', () => {
      resolve(true)
    })
    document.body.appendChild($script)
  })
}

